import {
  Alert,
  Button,
  CircularProgress,
  Stack,
  Typography,
  Fade,
  Slide,
  Divider,
  IconButton,
} from '@mui/material'
import Box from '@mui/material/Box'
import Drawer from '@mui/material/Drawer'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import { useEffect, useState } from 'react'
import { FormTextField, StyledButton } from './RoomModal'
import { Close, Delete } from '@mui/icons-material'

const BACKEND_URL = process.env.REACT_APP_SERVER_SOCKET

export default function PreviousRoomDrawer({
  open,
  setOpen,
  userEmail,
  handleJoinRoom,
  error,
}) {
  const [prevRooms, setPrevRooms] = useState([])
  const [loading, setLoading] = useState(false)
  const [roomIdInput, setRoomIdInput] = useState('')
  const [deleteError, setDeleteError] = useState('')

  useEffect(() => {
    if (!userEmail) return
    setLoading(true)
    const fetchPreviousRooms = async () => {
      try {
        const response = await fetch(
          `${BACKEND_URL}/api/room/prev-room/${userEmail}`
        )
        const data = await response.json()
        setPrevRooms(data?.prevRooms || [])
        setLoading(false)
      } catch (error) {
        console.log(error, 'error fetching previous room details')
      } finally {
        setLoading(false)
      }
    }
    fetchPreviousRooms()
  }, [userEmail])

  const handleJoin = (roomId) => {
    handleJoinRoom(roomId)
    setRoomIdInput('')
  }

  const handleDelete = async (roomId) => {
    setDeleteError('')
    try {
      const response = await fetch(`${BACKEND_URL}/api/room/delete/${roomId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail }),
      })
      if (!response.ok) throw new Error('Failed to remove room membership')
      setPrevRooms(prevRooms.filter((room) => room.room_id !== roomId))
    } catch (error) {
      setDeleteError('Failed to remove room')
      setTimeout(() => setDeleteError(''), 1500)
    }
  }

  const DrawerList = (
    <Fade in timeout={500}>
      <Box
        sx={{
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          backdropFilter: 'blur(16px)',
          backgroundColor: 'rgba(255, 255, 255, 0.06)',
          borderRight: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Typography
          variant='h5'
          sx={{
            fontWeight: 700,
            color: '#ffffff',
            textAlign: 'center',
            mb: 2,
          }}
        >
          Join a Room
        </Typography>
        <Typography
          sx={{
            position: 'absolute',
            color: 'white',
            right: '10px',
            top: '10px',
            cursor: 'pointer',
          }}
          onClick={() => setOpen(false)}
        >
          <Close />
        </Typography>
        <Stack spacing={2} mb={3}>
          <FormTextField
            variant='outlined'
            label='Room ID'
            value={roomIdInput}
            placeholder='room-mcocgspz-izl2a'
            onChange={(e) => setRoomIdInput(e.target.value)}
            sx={{
              '& .MuiInputLabel-root': { color: '#ccc' },
              input: { color: '#fff' },
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'rgba(255,255,255,0.3)',
                },
                '&:hover fieldset': {
                  borderColor: 'transparent',
                },
              },
            }}
          />
          {error && (
            <Alert
              severity='error'
              variant='outlined'
              sx={{
                backgroundColor: 'rgba(255, 0, 0, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#ff6b6b',
                backdropFilter: 'blur(8px)',
                fontWeight: 500,
                borderRadius: 2,
              }}
            >
              {error}
            </Alert>
          )}
          {deleteError && (
            <Alert
              severity='error'
              variant='outlined'
              sx={{
                backgroundColor: 'rgba(255, 0, 0, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#ff6b6b',
                backdropFilter: 'blur(8px)',
                fontWeight: 500,
                borderRadius: 2,
              }}
            >
              {deleteError}
            </Alert>
          )}
          <Button
            variant='contained'
            size='large'
            fullWidth
            disabled={roomIdInput?.length === 0}
            onClick={() => handleJoin(roomIdInput)}
            sx={{
              background: 'linear-gradient(135deg, #007bff, #00d4ff)',
              boxShadow: '0 0 12px rgba(0, 191, 255, 0.5)',
              color: '#fff',
              fontWeight: 600,
              borderRadius: 2,
              '&:hover': {
                background: 'linear-gradient(135deg, #005eff, #00b4ff)',
              },
            }}
          >
            Join Room
          </Button>
        </Stack>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)', mb: 2 }} />

        {prevRooms.length > 0 ? (
          <>
            <Typography
              variant='subtitle1'
              sx={{
                fontWeight: 600,
                mb: 1,
                textAlign: 'center',
                color: '#fff',
              }}
            >
              Previously Joined Rooms
            </Typography>

            <List sx={{ overflowY: 'auto' }}>
              {prevRooms.map((room) => (
                <Slide
                  key={room.id}
                  direction='left'
                  in
                  mountOnEnter
                  unmountOnExit
                >
                  <ListItem disablePadding sx={{ mb: 1 }}>
                    <ListItemButton
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        border: '1px solid rgba(255,255,255,0.1)',
                        backdropFilter: 'blur(12px)',
                        color: '#fff',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                      }}
                    >
                      <ListItemText
                        primary={room?.name}
                        primaryTypographyProps={{ color: '#fff' }}
                      />
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <StyledButton
                          variant='contained'
                          size='small'
                          onClick={() => handleJoin(room?.room_id)}
                        >
                          Join
                        </StyledButton>
                        <IconButton
                          onClick={() => handleDelete(room?.room_id)}
                          sx={{
                            color: '#ff6b6b',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 107, 107, 0.2)',
                            },
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </ListItemButton>
                  </ListItem>
                </Slide>
              ))}
            </List>
          </>
        ) : (
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              color: '#bbb',
              px: 2,
            }}
          >
            <Fade in timeout={600}>
              <Typography variant='body1'>
                You haven’t joined any rooms yet. Once you do, they’ll appear
                here!
              </Typography>
            </Fade>
          </Box>
        )}
      </Box>
    </Fade>
  )

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={() => setOpen(false)}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 450 },
          background: 'transparent',
          boxShadow: 'none',
        },
      }}
    >
      {loading ? (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
          }}
        >
          <CircularProgress color='inherit' />
          <Typography variant='caption' sx={{ mt: 2 }}>
            Loading previous rooms...
          </Typography>
        </Box>
      ) : (
        DrawerList
      )}
    </Drawer>
  )
}
